// Public shift-meditation detail page. URL: /m/shift/{shiftId}
// A meditation on a whole shift — the stories that happen around the griddle
// between the flips — with a collage of the day's pancakes. Sibling of the
// per-pancake MeditationDetail; shares its typography so the two read as one
// publication.
//
// Reads ONLY PII-safe public docs: publicShiftRecaps/{shiftId} for the
// meditation text (`shiftMeditation`) + pancake collage, config/publicMeditations
// for prev/next nav across shift meditations. Read-only, no auth.

import { useEffect, useState, useMemo } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { trackMeditationView } from "../../utils/analytics";
import SiteNav from "./components/SiteNav";
import SiteFooter from "./sections/SiteFooter";
import PanBar from "./components/PanBar";

export default function ShiftMeditationDetail({ shiftId, navigate }) {
  const [recap, setRecap]   = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [pool, setPool]     = useState([]); // shift meditations for prev/next

  useEffect(() => {
    if (!shiftId) return;
    let cancel = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "publicShiftRecaps", shiftId));
        if (!cancel) {
          setRecap(snap.exists() ? { shiftId: snap.id, ...snap.data() } : null);
          setLoaded(true);
          if (snap.exists()) trackMeditationView(`shift_${shiftId}`);
        }
      } catch {
        if (!cancel) setLoaded(true);
      }
    })();
    return () => { cancel = true; };
  }, [shiftId]);

  // Prev/next stays within shift meditations (the pancake pages keep their own
  // pancake-only chain) — one public config-doc read, no auth needed.
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "config/publicMeditations"), (snap) => {
      if (!snap.exists()) { setPool([]); return; }
      const rows = (snap.data()?.items || [])
        .filter((i) => i.type === "shift")
        .sort((a, b) =>
          (b.meditatedAtMs ?? b.completedAtMs ?? 0) - (a.meditatedAtMs ?? a.completedAtMs ?? 0));
      setPool(rows);
    }, () => {});
    return unsub;
  }, []);

  const idx = useMemo(() => pool.findIndex((i) => i.id === shiftId), [pool, shiftId]);
  const prev = idx > 0 ? pool[idx - 1] : null;
  const next = idx >= 0 && idx < pool.length - 1 ? pool[idx + 1] : null;

  const meditation = (recap?.shiftMeditation || "").trim();
  const published = !!recap && !recap._isTestData && meditation.length > 0;
  const title = recap?.eventName || recap?.venueName || "A day at the griddle";

  useEffect(() => {
    if (published) document.title = `${title} — Meditations · The Joy of Pancakes`;
    return () => { document.title = "The Joy of Pancakes"; };
  }, [published, title]);

  return (
    <div style={{ background: "#081a2a", minHeight: "100vh", color: "var(--jop-cream)", fontFamily: "var(--jop-display)" }}>
      <SiteNav />

      <main style={{ maxWidth: "780px", margin: "0 auto", padding: "5.5rem 1.5rem 4rem" }}>
        <button
          onClick={() => navigate("/m")}
          style={{
            background: "transparent",
            color: "rgba(255,218,173,0.6)",
            border: "1px solid rgba(255,218,173,0.2)",
            borderRadius: "999px",
            padding: "0.4rem 0.95rem",
            fontSize: "0.78rem",
            fontFamily: "inherit",
            fontWeight: 600,
            cursor: "pointer",
            marginBottom: "1.6rem",
          }}
        >
          ← All meditations
        </button>

        {!loaded && <p style={{ color: "rgba(255,218,173,0.5)", textAlign: "center", padding: "2rem" }}>Loading…</p>}

        {loaded && !published && <NotFound navigate={navigate} />}

        {loaded && published && <ShiftArticle recap={recap} title={title} meditation={meditation} navigate={navigate} />}

        {published && (prev || next) && (
          <PrevNextNav prev={prev} next={next} navigate={navigate} />
        )}
      </main>

      <SiteFooter />
    </div>
  );
}

function ShiftArticle({ recap, title, meditation, navigate }) {
  const pancakes = (recap.pancakes || [])
    .filter((p) => p.thumbUrl || p.cutoutUrl || p.photoUrl);
  const date = formatDate(recap.shiftMeditatedAtMs ?? recap.closedAtMs ?? recap.openedAtMs);
  const dayDate = formatDate(recap.openedAtMs ?? recap.closedAtMs);
  const count = recap.pancakeCount || pancakes.length;
  const venueLine = [dayDate, recap.venueName].filter(Boolean).join(" · ");

  return (
    <article style={{ display: "flex", flexDirection: "column", gap: "1.6rem" }}>
      {/* Collage hero — the day's pancakes drifting together. Organic varied
          sizes (like the Outro gallery); every third piece runs larger so the
          cluster never reads as a rigid grid. */}
      {pancakes.length > 0 && (
        <div style={{
          display: "flex", flexWrap: "wrap",
          alignItems: "center", justifyContent: "center",
          gap: "0.4rem",
          padding: "0.8rem 0.4rem",
        }}>
          {pancakes.slice(0, 10).map((p, i) => {
            const src = p.thumbUrl || p.cutoutUrl || p.photoUrl;
            const size = i % 3 === 0 ? 150 : i % 3 === 1 ? 110 : 126;
            return (
              <a key={p.orderId || i} href={`/gallery/${p.orderId}`} style={{ display: "block", lineHeight: 0 }}>
                <img
                  src={src}
                  alt={p.title || "A pancake"}
                  loading={i < 4 ? "eager" : "lazy"}
                  style={{
                    width: `${size}px`, height: `${size}px`,
                    objectFit: "contain",
                    filter: "drop-shadow(0 14px 28px rgba(0,0,0,0.55)) drop-shadow(0 4px 8px rgba(0,0,0,0.35))",
                    transform: `rotate(${((i * 7) % 11) - 5}deg)`,
                  }}
                />
              </a>
            );
          })}
        </div>
      )}

      <div style={{ textAlign: "center" }}>
        <PanBar width={64} height={3} margin="0 auto 1rem" />
        <div style={{
          color: "var(--jop-gold)",
          fontSize: "0.65rem",
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          fontWeight: 700,
          marginBottom: "0.5rem",
        }}>
          A griddle meditation
        </div>
        <h1 style={{
          margin: 0,
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(1.9rem, 4.5vw, 2.6rem)",
          color: "var(--jop-cream)",
          fontWeight: 700,
          lineHeight: 1.15,
        }}>
          {title}
        </h1>
        {(venueLine || count > 0) && (
          <div style={{
            marginTop: "0.55rem",
            color: "rgba(255,218,173,0.5)",
            fontSize: "0.78rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}>
            {venueLine}{venueLine && count > 0 ? " · " : ""}{count > 0 ? `${count} pancake${count === 1 ? "" : "s"}` : ""}
          </div>
        )}
      </div>

      {/* Meditation body — same voice box as the pancake pages */}
      <div style={{
        background: "rgba(252,179,31,0.04)",
        border: "1px solid rgba(252,179,31,0.18)",
        borderRadius: "14px",
        padding: "1.6rem 1.6rem 1.4rem",
      }}>
        <div style={{
          color: "var(--jop-gold)",
          fontSize: "0.65rem",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          fontWeight: 700,
          marginBottom: "0.8rem",
        }}>
          🥞 From Doctor Dan
        </div>
        <div style={{
          color: "var(--jop-cream)",
          fontSize: "clamp(1rem, 2vw, 1.12rem)",
          lineHeight: 1.7,
          whiteSpace: "pre-wrap",
        }}>
          {meditation}
        </div>
        {date && (
          <div style={{
            marginTop: "1rem",
            color: "rgba(255,218,173,0.4)",
            fontSize: "0.7rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}>
            Written {date}
          </div>
        )}
      </div>
    </article>
  );
}

function PrevNextNav({ prev, next, navigate }) {
  const label = (i) => (i.subjectName || "A day at the griddle").slice(0, 36);
  return (
    <div style={{
      marginTop: "3rem",
      paddingTop: "1.5rem",
      borderTop: "1px solid rgba(255,218,173,0.12)",
      display: "flex",
      justifyContent: "space-between",
      gap: "1rem",
      flexWrap: "wrap",
    }}>
      {prev ? (
        <button onClick={() => navigate(`/m/shift/${prev.id}`)} style={navBtnStyle}>
          ← {label(prev)}
        </button>
      ) : <span />}
      {next ? (
        <button onClick={() => navigate(`/m/shift/${next.id}`)} style={{ ...navBtnStyle, textAlign: "right" }}>
          {label(next)} →
        </button>
      ) : <span />}
    </div>
  );
}

function NotFound({ navigate }) {
  return (
    <div style={{
      textAlign: "center",
      padding: "3rem 1rem",
      color: "rgba(255,218,173,0.6)",
      fontFamily: "inherit",
    }}>
      <p style={{ marginBottom: "1.2rem" }}>This meditation isn't published, or doesn't exist.</p>
      <button onClick={() => navigate("/m")} style={navBtnStyle}>
        ← All meditations
      </button>
    </div>
  );
}

const navBtnStyle = {
  background: "rgba(252,179,31,0.06)",
  color: "var(--jop-gold)",
  border: "1px solid rgba(252,179,31,0.3)",
  borderRadius: "10px",
  padding: "0.65rem 1rem",
  fontFamily: "inherit",
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
  flex: "1 1 auto",
  maxWidth: "48%",
};

function formatDate(ms) {
  if (!ms) return null;
  const d = new Date(ms);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}
